import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { StoreResponse } from '@clarity/contract';
import { useOrders, usePayouts } from '../../queries/index.js';
import { useScopeContextOptional } from '../../lib/scopeContext.js';
import { QueryState } from '../QueryState.js';
import { isOnboardingRequired } from '../format.js';
import { OnboardingBlocked, PayoutDetailBody } from './shared.js';

/**
 * The standalone PAYOUT DETAIL PAGE at
 * `/c/:clientId/store/:storeId/payouts/:payoutId` — a payout's own page, not a
 * modal (replaces the old `?payout=` {@link PayoutDetailBody} modal). It is a
 * sibling route of the six workspace surfaces under the `store/:storeId` scope,
 * so it mounts INSIDE the `<ScopeProvider>` + `OnboardingLockGuard`: the scope
 * context resolves the single branch, and a not-yet-onboarded branch never
 * reaches here (the lock forces onboarding first).
 *
 * It reads the same data the modal did, from reads already warm in the cache:
 * - the raw payout keyed by its uuid `id` (the `:payoutId` segment) from
 *   `usePayouts` — carrying `linkedOrderIds`, `vouchersByBrand`, `marketingAds`,
 *   and the figures the body shows;
 * - the store's orders (`useOrders`), indexed by id so the body can resolve the
 *   payout's settled orders for the paged drill-down.
 *
 * The body ({@link PayoutDetailBody}) is unchanged in substance — it is the exact
 * content that used to render inside the modal, now framed as a page with a Back
 * link and the payout id as the page heading. "Back to payouts" and each settled
 * order link navigate across pages (same store scope), so the whole thing is
 * linkable and part of the browser history.
 */
export function StorePayoutDetail({ store: storeProp }: { store?: StoreResponse }) {
  const { clientId, storeId, payoutId } = useParams();
  const navigate = useNavigate();

  // The store comes from the scope context at a store scope (same source the
  // workspace surfaces use); a passed `store` prop wins when provided (tests
  // that mount the page outside a ScopeProvider). `useScopeContextOptional`
  // returns null outside a provider instead of throwing.
  const scope = useScopeContextOptional();
  const store = storeProp ?? scope?.store ?? null;

  const payouts = usePayouts(store?.id ?? '', Boolean(store));
  const orders = useOrders(store?.id ?? '', Boolean(store));

  const payout = useMemo(() => {
    const list = payouts.data?.status === 200 ? payouts.data.body : [];
    return list.find((p) => p.id === payoutId);
  }, [payouts.data, payoutId]);

  const ordersById = useMemo(() => {
    const list = orders.data?.status === 200 ? orders.data.body : [];
    return new Map(list.map((r) => [r.order.id, r.order]));
  }, [orders.data]);

  const backToPayouts = () => {
    if (clientId && storeId) navigate(`/c/${clientId}/store/${storeId}/payouts`);
  };

  // Open a settled order in the Orders surface (same store scope + `?order=`),
  // the reverse of the order→payout trace.
  const openOrder = (orderId: string) => {
    if (clientId && storeId) {
      navigate(`/c/${clientId}/store/${storeId}/orders?order=${orderId}`);
    }
  };

  // The onboarding gate (defense-in-depth; the OnboardingLockGuard is primary).
  if (isOnboardingRequired(payouts.error)) return <OnboardingBlocked />;

  return (
    <section className="card payout-detail-page" aria-label="Payout detail">
      <button type="button" onClick={backToPayouts}>
        ← Back to payouts
      </button>
      <QueryState
        isLoading={payouts.isLoading || orders.isLoading}
        isError={payouts.isError || orders.isError}
        label="payout"
      >
        {!store || payout === undefined ? (
          <p className="muted">Payout not found.</p>
        ) : (
          <>
            <header className="payout-detail-page__head">
              <h3>Payout {payout.externalPayoutId}</h3>
            </header>
            <PayoutDetailBody
              store={store}
              payout={payout}
              ordersById={ordersById}
              onOpenOrder={openOrder}
            />
          </>
        )}
      </QueryState>
    </section>
  );
}
